<template>
    <table class="people-table fs-mask" :class="{ 'without-profiles': !publicProfileEnabled }" :aria-label="t('peopleDirectory.people')">
        <thead>
            <tr>
                <th scope="col">{{ t("peopleDirectory.name") }}</th>
                <th v-if="publicProfileEnabled" scope="col">{{ t("peopleDirectory.profile") }}</th>
                <th scope="col">
                    <span class="sr-only">{{ t("peopleDirectory.actions") }}</span>
                </th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="person in connections" :key="person.id">
                <td>
                    <div class="flex items-center gap-2 md:gap-3 min-w-0">
                        <BaseCheckbox
                            v-if="selectable"
                            shape="square"
                            :model-value="selectedIds?.has(person.id) ?? false"
                            :aria-label="t('people.bulkDelete.selectPerson', { name: person.persona?.name || t('peopleDirectory.unnamed') })"
                            @update:model-value="emit('toggleSelect', person.id)" />
                        <PeopleAvatar :name="person.persona?.name" :disabled="dismissed || person.is_inactive" :size="isMobile ? 'xs' : 'sm'" aria-hidden="true" />
                        <div class="min-w-0">
                            <span v-if="selectable" class="nadia-chat-bold md:nadia-subtitle-small text-ink break-words">{{
                                person.persona?.name || t("peopleDirectory.unnamed")
                            }}</span>
                            <BaseLink
                                v-else
                                :href="personUrl(person.id)"
                                class="people-name nadia-chat-bold md:nadia-subtitle-small text-ink break-words hover:underline"
                                @click="emit('details', person)">
                                {{ person.persona?.name || t("peopleDirectory.unnamed") }}
                            </BaseLink>
                            <p v-if="person.connection.job_title" class="details md:body-small text-ink-muted break-words">{{ person.connection.job_title }}</p>
                            <div v-if="person.is_hris || person.is_inactive" class="flex flex-wrap items-center gap-2 mt-1">
                                <span v-if="person.is_hris" class="scaffold-details-small text-ink-muted"
                                    ><i class="bi bi-building mr-1" aria-hidden="true" />{{ t("peopleDirectory.hris") }}</span
                                >
                                <StatusChip v-if="person.is_inactive" state="neutral" size="xs">{{ t("peopleDirectory.inactive") }}</StatusChip>
                            </div>
                        </div>
                    </div>
                </td>
                <td v-if="publicProfileEnabled">
                    <BaseLink
                        v-if="person.public_profile"
                        :href="profileUrlTemplate.replace(zeroUuid, person.public_profile.user_id)"
                        class="relative z-10 details md:body-small inline-flex items-center gap-1 md:gap-2 text-ink underline underline-offset-4"
                        :aria-label="t('peopleDirectory.viewProfile', { name: person.persona?.name || t('peopleDirectory.unnamed') })">
                        <img :src="CollabFilledIcon" alt="" class="h-4 w-4 shrink-0" /><span>{{
                            isMobile ? t("peopleDirectory.profileShort") : t("peopleDirectory.viewProfileShort")
                        }}</span>
                    </BaseLink>
                    <span v-else class="details text-ink-muted">{{ isMobile ? t("peopleDirectory.noProfileShort") : t("peopleDirectory.noProfile") }}</span>
                </td>
                <td>
                    <div v-if="!selectable" class="people-row-actions relative z-10 flex items-center gap-0 md:gap-2 justify-end">
                        <BaseLink
                            v-if="dismissed"
                            :href="personUrl(person.id)"
                            class="hidden md:inline body-small text-ink underline underline-offset-4"
                            @click="emit('details', person)"
                            >{{ t("peopleDirectory.restore") }}</BaseLink
                        >
                        <template v-else>
                            <BaseDropdownMenu :sections="menuSections" @select="(item) => emitAction(item.id, person)">
                                <template #trigger>
                                    <BaseButton
                                        :theme="isMobile ? 'ghost' : 'neutral'"
                                        size="sm"
                                        shape="square"
                                        icon-only
                                        :disabled="busyId === person.id"
                                        :aria-label="t('peopleDirectory.optionsFor', { name: person.persona?.name || t('peopleDirectory.unnamed') })">
                                        <i class="bi bi-three-dots" aria-hidden="true" />
                                    </BaseButton>
                                </template>
                            </BaseDropdownMenu>
                        </template>
                        <BaseButton
                            :theme="isMobile ? 'ghost' : 'neutral'"
                            size="sm"
                            shape="square"
                            icon-only
                            :aria-label="t('peopleDirectory.detailsFor', { name: person.persona?.name || t('peopleDirectory.unnamed') })"
                            @click="emit('open', person)">
                            <i class="bi bi-chevron-right" aria-hidden="true" />
                        </BaseButton>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import CollabFilledIcon from "~img/collab_icons/collab-filled.svg";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import type { ConnectionDict } from "~vue/types/connection";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const {
    selectable = false,
    selectedIds,
    connections,
    dismissed,
    publicProfileEnabled,
    busyId,
    detailsUrlTemplate,
    editUrlTemplate,
    profileUrlTemplate,
} = defineProps<{
    selectable?: boolean;
    selectedIds?: ReadonlySet<string>;
    connections: ConnectionDict[];
    dismissed: boolean;
    publicProfileEnabled: boolean;
    busyId: string | null;
    detailsUrlTemplate: string;
    editUrlTemplate: string;
    profileUrlTemplate: string;
}>();
const emit = defineEmits<{
    toggleSelect: [id: string];
    details: [person: ConnectionDict];
    open: [person: ConnectionDict];
    chat: [person: ConnectionDict];
    rolePlay: [person: ConnectionDict];
    edit: [person: ConnectionDict];
    merge: [person: ConnectionDict];
    delete: [person: ConnectionDict];
}>();
const { t } = useI18n();
const isMobile = useBreakpoints(breakpointsTailwind).smaller("md");
const zeroUuid = "00000000-0000-0000-0000-000000000000";
const personUrl = (id: string) => (dismissed ? editUrlTemplate : detailsUrlTemplate).replace(zeroUuid, id);
const menuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "actions",
        items: [
            { id: "rolePlay", type: "button", label: t("peopleDirectory.rolePlay"), icon: "bi bi-person-video3" },
            { id: "chat", type: "button", label: t("peopleDirectory.chat"), icon: "bi bi-chat" },
            { id: "edit", type: "button", label: t("peopleDirectory.edit"), icon: "bi bi-pencil" },
            { id: "merge", type: "button", label: t("peopleDirectory.merge"), icon: "bi bi-intersect" },
        ],
    },
    { id: "danger", items: [{ id: "delete", type: "button", label: t("peopleDirectory.delete"), icon: "bi bi-trash", theme: "danger" }] },
]);
function emitAction(action: string, person: ConnectionDict) {
    switch (action) {
        case "rolePlay":
            emit("rolePlay", person);
            break;
        case "chat":
            emit("chat", person);
            break;
        case "edit":
            emit("edit", person);
            break;
        case "merge":
            emit("merge", person);
            break;
        case "delete":
            emit("delete", person);
            break;
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .people-table {
        @apply w-full border-separate border-spacing-0 rounded-lgr border border-border bg-surface-raised;
    }
    .people-table thead {
        @apply hidden md:table-header-group;
    }
    .people-table th {
        @apply scaffold-details-small text-ink-muted text-left px-5 py-3 border-b border-border;
    }
    .people-table tbody {
        @apply block md:table-row-group;
    }
    .people-table tr {
        @apply relative grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 px-3 py-2.5 min-h-16 border-b border-border last:border-b-0 md:table-row md:p-0;
    }
    .people-table td {
        @apply block min-w-0 md:table-cell md:px-5 md:py-4 md:border-b md:border-border;
    }
    .people-name::after {
        content: "";
        @apply absolute inset-0;
    }
    .people-table tbody tr:last-child td {
        @apply border-b-0;
    }
    .people-table tbody tr {
        @apply hover:bg-surface focus-within:bg-surface;
    }
    .people-table.without-profiles tr {
        @apply grid-cols-[minmax(0,1fr)_auto];
    }
    .people-table td:first-child {
        @apply md:w-2/3;
    }
    .people-table td:last-child {
        @apply md:w-px md:whitespace-nowrap;
    }
    @media (min-width: 768px) and (hover: hover) and (pointer: fine) {
        .people-row-actions {
            @apply opacity-0 pointer-events-none;
        }
        .people-table tr:hover .people-row-actions,
        .people-table tr:focus-within .people-row-actions,
        .people-row-actions:has([aria-expanded="true"]) {
            @apply opacity-100 pointer-events-auto;
        }
    }
}
</style>
